// Async specify-job client + dev mock.
//
// The dedicated SpecifyRunScreen drives the agentic specification by polling an
// async job. The product flow is: POST the reviewed specify payload to start a
// job, then GET the job snapshot every ~1.5s until it is no longer "running".
// There is NO client timeout cap — a real multi-apartment-type run can take as
// long as it needs; the page simply keeps watching.
//
// This module pins the exact job contract a parallel backend agent implements
// for real, and ships a local DEV MOCK (behind VITE_SPECIFY_MOCK / an explicit
// option) that produces the same progress shape so the animation can be driven
// end-to-end before the real endpoints are merged. The mock emits a realistic
// propose -> verify -> revise -> emit loop per apartment type with live tool
// calls, then an overseer aggregation phase, and finishes by handing back a
// genuine workspace fixture computed from the same input (never a fake success).

import type { SpecifyProjectInput } from "./specifyProject";
import { withAuthHeaders } from "../browser/apiAuthHeaders";

export type SpecifyJobStatus =
  | "running"
  | "succeeded"
  | "failed"
  | "partial"
  | "interrupted";

export type SpecifyTypeStatus =
  | "pending"
  | "running"
  | "verifying"
  | "revising"
  | "done"
  /**
   * Track 1: terminal and usable, but explicitly NOT CERTIFIED. It must never
   * be rendered with the same "Ready" affordance as a certified type.
   */
  | "draft"
  | "failed"
  | "stopped";

export type SpecifyToolStatus = "running" | "ok" | "advisory" | "error";
export type SpecifyFeedVerdictStatus =
  | SpecifyToolStatus
  | "pass"
  | "advisory"
  | "violation"
  | "blocker";

export type SpecifyOverseerStatus = "pending" | "running" | "done";

export type SpecifyAggregationStage =
  | "cross_type_review"
  | "cross_type_recheck"
  | "cross_type_rereview"
  | "project_overseer";

export type SpecifyLogKind =
  | "type_started"
  | "tool_call"
  | "verifier"
  | "revise"
  | "type_done"
  | "aggregating"
  | "done"
  | "error";

export interface SpecifyToolCall {
  name: string;
  status: SpecifyToolStatus;
  detail?: string;
}

export interface SpecifyTypeProgress {
  apartmentTypeId: string;
  label: string;
  status: SpecifyTypeStatus;
  iteration: number;
  toolCalls: SpecifyToolCall[];
  lastMessage: string;
}

export interface SpecifyOverseer {
  status: SpecifyOverseerStatus;
  message?: string;
  stage?: SpecifyAggregationStage;
  pass?: number;
  passCount?: number;
  canStillChange?: string;
}

export interface SpecifyCompletedType {
  apartmentTypeId: string;
  completedAt: string;
  solutionHash: string;
  source: "specialist" | "aggregation_recheck";
  aggregationPass?: number;
  solution: unknown;
}

export interface SpecifyLogEntry {
  seq: number;
  ts: string;
  kind: SpecifyLogKind;
  code?: string;
  apartmentTypeId?: string;
  toolName?: string;
  toolStatus?: SpecifyToolStatus;
  verdictStatus?: SpecifyFeedVerdictStatus;
  status?: SpecifyFeedVerdictStatus;
  verifierStatus?: string;
  iteration?: number;
  maxIterations?: number;
  checkLabel?: string;
  failureKind?: string;
  message: string;
}

export interface SpecifyJobProgress {
  phase: "specifying" | "aggregating" | "done";
  types: SpecifyTypeProgress[];
  completedTypes?: SpecifyCompletedType[];
  overseer: SpecifyOverseer;
  log: SpecifyLogEntry[];
  /** Total emitted technical events; the retained log may be truncated. */
  nextSeq?: number;
}

export interface SpecifyTerminalFinding {
  code: string;
  severity: string;
  message: string;
  details?: Record<string, unknown>;
  machineSummary?: unknown;
  verifierResultId?: string;
  checkKind?: string;
}

export interface SpecifyLiveSpecFailedType {
  apartmentTypeId: string;
  failureKind?: string;
  errorName?: string;
  errorMessage?: string;
  traceIds?: string[];
  terminalFindings?: SpecifyTerminalFinding[];
}

export interface SpecifyJobLiveSpecError {
  kind?: string;
  failureKind?: string;
  failureKinds?: string[];
  failedApartmentTypeIds?: string[];
  stoppedApartmentTypeIds?: string[];
  completedApartmentTypeIds?: string[];
  failedTypes?: SpecifyLiveSpecFailedType[];
}

export type ProviderPauseCallClass =
  | "copilot"
  | "gemini-smoke"
  | "pdf-parse"
  | "pasted-text-parse"
  | "project-extraction"
  | "requirement-extraction"
  | "policy-flag-discovery"
  | "focused-flag-recheck"
  | "specify-preflight"
  | "spec-agent-design-call"
  | "spec-agent-revision-call"
  | "spec-overseer"
  | "spec-global-aggregator"
  | "test"
  | "unknown";

export interface SpecifyJobBudgetSnapshot {
  unmeteredFailureWindowMs?: number;
  unmeteredFailureWindowCount?: number;
  unmeteredFailureProjectCap?: number;
  unmeteredFailureCapRemaining?: number;
  unmeteredFailureCallClass?: ProviderPauseCallClass;
  unmeteredFailureCallClassLabel?: string;
  degradationReason?: string;
  [key: string]: unknown;
}

export interface SpecifyJobError {
  code: string;
  message: string;
  liveSpecError?: SpecifyJobLiveSpecError;
  liveProviderError?: unknown;
  retryDetails?: unknown;
  budget?: SpecifyJobBudgetSnapshot;
}

export interface SpecifyJobAdvisoryType {
  apartmentTypeId: string;
  terminalFindings: SpecifyTerminalFinding[];
}

export interface SpecifyJobAdvisories {
  types: SpecifyJobAdvisoryType[];
}

export interface SpecifyJobSnapshot {
  jobId: string;
  status: SpecifyJobStatus;
  createdAt: string;
  updatedAt: string;
  progress: SpecifyJobProgress;
  error?: SpecifyJobError;
  advisories?: SpecifyJobAdvisories;
  // The final spec result. Present when status === "succeeded" and for partial
  // jobs that preserved a verified subset. The real backend returns the SYNC
  // /api/specify-live response WRAPPER here, not a bare fixture — see
  // SpecifyJobResultWrapper. Use extractSpecifyResultFixture() to get the
  // ProjectFixtureWithLaneB the workspace consumes.
  result?: unknown;
}

/**
 * The async-job `result` wrapper the real backend emits (mirrors the sync
 * /api/specify-live response). The full ProjectFixtureWithLaneB is nested under
 * `.fixture`; the overseer's building-level aggregation is under `.overseer`.
 */
export interface SpecifyJobResultWrapper {
  ok?: boolean;
  provider?: { mode?: string; specifierId?: string };
  projectId?: string;
  fixture: unknown;
  overseer?: SpecifyResultOverseer;
  driftClassification?: unknown;
  budget?: unknown;
}

export interface SpecifyResultOverseer {
  summary?: string;
  buildingInfrastructure?: unknown[];
  crossTypeFindings?: unknown[];
}

/**
 * Defensively pull the ProjectFixtureWithLaneB out of a job result. Handles BOTH
 * shapes: the real backend's wrapper ({ fixture, overseer, ... }) and a bare
 * fixture. NOTE: a bare ProjectFixtureWithLaneB itself has a top-level `.fixture`
 * *metadata* block, so we discriminate on top-level `apartmentSolutions` (only a
 * bare fixture has it) rather than on the presence of a `fixture` key.
 */
export function extractSpecifyResultFixture(result: unknown): unknown {
  if (!result || typeof result !== "object") {
    return result;
  }

  const record = result as Record<string, unknown>;

  if ("apartmentSolutions" in record) {
    return result;
  }

  if ("fixture" in record && record.fixture && typeof record.fixture === "object") {
    return record.fixture;
  }

  return result;
}

/** Pull the overseer building-level aggregation from a wrapper result, if present. */
export function extractSpecifyResultOverseer(
  result: unknown
): SpecifyResultOverseer | undefined {
  if (!result || typeof result !== "object" || !("overseer" in result)) {
    return undefined;
  }

  const overseer = (result as { overseer?: unknown }).overseer;

  if (overseer && typeof overseer === "object") {
    return overseer as SpecifyResultOverseer;
  }

  return undefined;
}

export interface SpecifyJobClientOptions {
  fetcher?: typeof fetch;
  /** Force the in-browser mock on/off. Defaults to the VITE_SPECIFY_MOCK env. */
  mock?: boolean;
}

export interface PollSpecifyJobOptions extends SpecifyJobClientOptions {
  intervalMs?: number;
  signal?: AbortSignal;
  onUpdate?: (snapshot: SpecifyJobSnapshot) => void;
}

export type SpecifyJobCancelResult =
  | {
      ok: true;
      jobId: string;
      cancelled: true;
    }
  | {
      ok: true;
      jobId: string;
      cancelled: false;
      status: SpecifyJobStatus;
    };

export class SpecifyJobAbortError extends Error {
  constructor() {
    super("Specification watching was cancelled.");
    this.name = "SpecifyJobAbortError";
  }
}

export class SpecifyJobStartError extends Error {
  readonly code: string | undefined;
  readonly budget: SpecifyJobBudgetSnapshot | undefined;
  readonly liveProviderError?: unknown;

  constructor(input: {
    message: string;
    code?: string;
    budget?: SpecifyJobBudgetSnapshot;
    liveProviderError?: unknown;
  }) {
    super(input.message);
    this.name = "SpecifyJobStartError";
    this.code = input.code;
    this.budget = input.budget;
    this.liveProviderError = input.liveProviderError;
  }
}

const DEFAULT_POLL_INTERVAL_MS = 1500;
const JOBS_ENDPOINT = "/api/specify-live/jobs";
const PROJECTS_ENDPOINT = "/api/projects";
const ACTIVE_JOB_STORAGE_PREFIX = "automatry.specify.activeJob.";

export const specifyJobStorageKey = (projectId: string): string =>
  `${ACTIVE_JOB_STORAGE_PREFIX}${projectId}`;

const browserStorage = (): Storage | undefined => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
};

export function readPersistedSpecifyJobId(
  projectId: string,
  storage: Storage | undefined = browserStorage()
): string | null {
  if (!projectId || !storage) {
    return null;
  }

  const value = storage.getItem(specifyJobStorageKey(projectId))?.trim();

  return value ? value : null;
}

export function persistSpecifyJobId(
  projectId: string,
  jobId: string,
  storage: Storage | undefined = browserStorage()
): void {
  if (!projectId || !jobId || !storage) {
    return;
  }

  storage.setItem(specifyJobStorageKey(projectId), jobId);
}

export function clearPersistedSpecifyJobId(
  projectId: string,
  jobId?: string,
  storage: Storage | undefined = browserStorage()
): void {
  if (!projectId || !storage) {
    return;
  }

  const key = specifyJobStorageKey(projectId);

  if (jobId) {
    const current = storage.getItem(key);

    if (current !== jobId) {
      return;
    }
  }

  storage.removeItem(key);
}

export function isSpecifyMockEnabled(): boolean {
  // Vite inlines import.meta.env.*; guarded so non-Vite test runners are safe.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> })
    .env;

  return env?.VITE_SPECIFY_MOCK === "1" || env?.VITE_SPECIFY_MOCK === "true";
}

const useMock = (options?: SpecifyJobClientOptions): boolean =>
  options?.mock ?? isSpecifyMockEnabled();

/** Start an async specification job. Returns its id; poll for progress. */
export async function startSpecifyJob(
  input: SpecifyProjectInput,
  options?: SpecifyJobClientOptions
): Promise<{ jobId: string }> {
  if (useMock(options)) {
    return startMockSpecifyJob(input);
  }

  const fetcher = options?.fetcher ?? fetch;
  const requestBody: SpecifyProjectInput = {
    projectId: input.projectId,
    projectName: input.projectName,
    apartmentTypes: input.apartmentTypes,
    ...(input.policyFlagSet ? { policyFlagSet: input.policyFlagSet } : {})
  };
  const response = await fetcher(JOBS_ENDPOINT, {
    method: "POST",
    headers: await withAuthHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(requestBody)
  });
  const payload = (await response.json().catch(() => ({}))) as {
    jobId?: string;
    error?: string;
    code?: string;
    budget?: SpecifyJobBudgetSnapshot;
    liveProviderError?: unknown;
  };

  if (!response.ok || !payload.jobId) {
    throw new SpecifyJobStartError({
      message:
        payload.error ?? `Could not start specification (HTTP ${response.status}).`,
      ...(payload.code ? { code: payload.code } : {}),
      ...(payload.budget ? { budget: payload.budget } : {}),
      ...(payload.liveProviderError
        ? { liveProviderError: payload.liveProviderError }
        : {})
    });
  }

  return { jobId: payload.jobId };
}

export async function clearProviderPause(
  projectId: string,
  input: { callClass?: ProviderPauseCallClass },
  options?: SpecifyJobClientOptions
): Promise<{
  ok: boolean;
  projectId: string;
  callClass?: ProviderPauseCallClass;
  clearedAt?: string;
  budget?: SpecifyJobBudgetSnapshot;
}> {
  if (useMock(options)) {
    return {
      ok: true,
      projectId,
      ...(input.callClass ? { callClass: input.callClass } : {}),
      clearedAt: new Date().toISOString()
    };
  }

  const fetcher = options?.fetcher ?? fetch;
  const response = await fetcher(
    `${PROJECTS_ENDPOINT}/${encodeURIComponent(projectId)}/provider-pause`,
    {
      method: "POST",
      headers: await withAuthHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(input)
    }
  );
  const payload = (await response.json().catch(() => ({}))) as {
    ok?: boolean;
    projectId?: string;
    callClass?: ProviderPauseCallClass;
    clearedAt?: string;
    budget?: SpecifyJobBudgetSnapshot;
    error?: string;
  };

  if (!response.ok || payload.ok !== true || !payload.projectId) {
    throw new Error(
      payload.error ?? `Could not clear provider pause (HTTP ${response.status}).`
    );
  }

  return {
    ok: true,
    projectId: payload.projectId,
    ...(payload.callClass ? { callClass: payload.callClass } : {}),
    ...(payload.clearedAt ? { clearedAt: payload.clearedAt } : {}),
    ...(payload.budget ? { budget: payload.budget } : {})
  };
}

/** Read a single job snapshot. */
export async function fetchSpecifyJob(
  jobId: string,
  options?: SpecifyJobClientOptions
): Promise<SpecifyJobSnapshot> {
  if (useMock(options)) {
    return readMockSpecifyJob(jobId);
  }

  const fetcher = options?.fetcher ?? fetch;
  const response = await fetcher(`${JOBS_ENDPOINT}/${encodeURIComponent(jobId)}`, {
    method: "GET",
    headers: await withAuthHeaders({ accept: "application/json" })
  });
  const payload = (await response.json().catch(() => ({}))) as
    | SpecifyJobSnapshot
    | { error?: string };

  if (!response.ok || !("status" in payload)) {
    const message =
      "error" in payload && typeof payload.error === "string"
        ? payload.error
        : `Could not read specification job (HTTP ${response.status}).`;

    throw new Error(message);
  }

  return payload;
}

/** Ask the server to stop a running job and report the durable outcome honestly. */
export async function cancelSpecifyJob(
  jobId: string,
  options?: SpecifyJobClientOptions
): Promise<SpecifyJobCancelResult> {
  if (useMock(options)) {
    if (!mockJobs.delete(jobId)) {
      throw new Error(`No specification job with id ${jobId}.`);
    }

    return { ok: true, jobId, cancelled: true };
  }

  const fetcher = options?.fetcher ?? fetch;
  const response = await fetcher(`${JOBS_ENDPOINT}/${encodeURIComponent(jobId)}`, {
    method: "DELETE",
    headers: await withAuthHeaders({ accept: "application/json" })
  });
  const payload = (await response.json().catch(() => ({}))) as {
    ok?: boolean;
    jobId?: string;
    cancelled?: boolean;
    status?: SpecifyJobStatus;
    error?: string;
  };

  if (
    !response.ok ||
    payload.ok !== true ||
    payload.jobId !== jobId ||
    typeof payload.cancelled !== "boolean"
  ) {
    throw new Error(
      payload.error ?? `Could not cancel specification job (HTTP ${response.status}).`
    );
  }

  if (payload.cancelled) {
    return { ok: true, jobId, cancelled: true };
  }

  if (!payload.status) {
    throw new Error("The server did not report the job state after cancellation lost.");
  }

  return {
    ok: true,
    jobId,
    cancelled: false,
    status: payload.status
  };
}

const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new SpecifyJobAbortError());
      return;
    }

    const timer = globalThis.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      globalThis.clearTimeout(timer);
      reject(new SpecifyJobAbortError());
    };

    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * Poll a job until it stops running. Calls onUpdate with every snapshot (so the
 * UI can animate each frame), and resolves with the terminal snapshot. No
 * timeout: the loop ends only on a terminal status or an abort signal.
 */
export async function pollSpecifyJob(
  jobId: string,
  options?: PollSpecifyJobOptions
): Promise<SpecifyJobSnapshot> {
  const intervalMs = options?.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const signal = options?.signal;

  for (;;) {
    if (signal?.aborted) {
      throw new SpecifyJobAbortError();
    }

    const snapshot = await fetchSpecifyJob(jobId, options);

    options?.onUpdate?.(snapshot);

    if (snapshot.status !== "running") {
      return snapshot;
    }

    await delay(intervalMs, signal);
  }
}

// ---------------------------------------------------------------------------
// DEV MOCK
//
// A self-contained, in-memory job emitter that reproduces the contract above.
// It is deterministic given a start time, so polling at any cadence yields a
// consistent, monotonic progress feed. It runs the real local engine to produce
// a genuine result fixture — so on success the workspace loads real data.
// ---------------------------------------------------------------------------

type MockOp =
  | {
      kind: "type_status";
      id: string;
      status: SpecifyTypeStatus;
      iteration?: number;
      lastMessage?: string;
    }
  | { kind: "tool_start"; id: string; name: string; detail?: string }
  | { kind: "tool_finish"; id: string; name: string; status: "ok" | "error" }
  | { kind: "overseer"; value: SpecifyOverseer }
  | { kind: "phase"; phase: SpecifyJobProgress["phase"] }
  | { kind: "log"; entry: Omit<SpecifyLogEntry, "seq" | "ts"> };

interface MockEvent {
  atMs: number;
  ops: MockOp[];
}

interface MockJob {
  jobId: string;
  createdAtMs: number;
  totalMs: number;
  events: MockEvent[];
  labels: { id: string; label: string }[];
  result?: unknown;
  error?: SpecifyJobError;
}

const mockJobs = new Map<string, MockJob>();

const PROPOSE_TOOLS: { name: string; detail: string }[] = [
  {
    name: "analyse_requirements",
    detail: "Reading approved requirements and strategy flags"
  },
  {
    name: "analyse_spaces",
    detail: "Mapping rooms to control zones"
  },
  {
    name: "pack_controllers",
    detail: "Bin-packing zones onto controllers (logical room grouping)"
  }
];

async function startMockSpecifyJob(
  input: SpecifyProjectInput
): Promise<{ jobId: string }> {
  const jobId = `mock-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  const createdAtMs = Date.now();
  const labels = input.apartmentTypes.map((apartmentType) => ({
    id: apartmentType.id,
    label: apartmentType.name || apartmentType.id
  }));

  let result: unknown;
  let error: SpecifyJobError | undefined;

  try {
    // Engine import is dynamic so production (mock-off) keeps it out of this path.
    const { specifyProject } = await import("./specifyProject");

    // Match the REAL backend: the job result is the sync-response WRAPPER, with
    // the ProjectFixtureWithLaneB nested under `.fixture` — so the mock exercises
    // the exact same extraction path the production backend does.
    result = buildMockResultWrapper(input, specifyProject(input));
  } catch (caught) {
    error = {
      code: "specify_failed",
      message:
        caught instanceof Error
          ? caught.message
          : "The specification engine could not run this project."
    };
  }

  const plan = error
    ? buildMockFailurePlan(labels, error)
    : buildMockSuccessPlan(labels);

  mockJobs.set(jobId, {
    jobId,
    createdAtMs,
    totalMs: plan.totalMs,
    events: plan.events,
    labels,
    ...(result !== undefined ? { result } : {}),
    ...(error ? { error } : {})
  });

  return { jobId };
}

function buildMockResultWrapper(
  input: SpecifyProjectInput,
  fixture: unknown
): SpecifyJobResultWrapper {
  const typeCount = input.apartmentTypes.length;
  const totalApartments = input.apartmentTypes.reduce(
    (sum, type) => sum + (Number.isFinite(type.homes) ? type.homes : 0),
    0
  );
  const typeWord = typeCount === 1 ? "type" : "types";
  const aptWord = totalApartments === 1 ? "apartment" : "apartments";

  return {
    ok: true,
    provider: { mode: "mock", specifierId: "mock" },
    projectId: input.projectId,
    fixture,
    overseer: {
      summary: `Aggregated ${typeCount} apartment ${typeWord} across ${totalApartments} ${aptWord} into one project specification.`,
      buildingInfrastructure: [
        {
          label: "Shared Netmaster head-ends",
          detail: `Consolidated across ${totalApartments} ${aptWord}`
        }
      ],
      crossTypeFindings: [
        {
          label: "Controller part-number consistency",
          detail: "All apartment types standardised on TP-C-710R-IP"
        }
      ]
    },
    budget: { inputTokens: 0, outputTokens: 0 }
  };
}

function buildMockSuccessPlan(labels: { id: string; label: string }[]): {
  events: MockEvent[];
  totalMs: number;
} {
  const events: MockEvent[] = [];
  const STAGGER_MS = 1400;
  const TOOL_MS = 820;
  let lastTypeEndMs = 0;

  labels.forEach((type, index) => {
    const base = index * STAGGER_MS;
    let t = base;

    events.push({
      atMs: t,
      ops: [
        {
          kind: "type_status",
          id: type.id,
          status: "running",
          iteration: 1,
          lastMessage: "Proposing the controls specification"
        },
        {
          kind: "log",
          entry: {
            kind: "type_started",
            apartmentTypeId: type.id,
            message: `Specifying ${type.label}`
          }
        }
      ]
    });

    // Propose tool calls.
    for (const tool of PROPOSE_TOOLS) {
      t += TOOL_MS;
      events.push({
        atMs: t,
        ops: [
          { kind: "tool_start", id: type.id, name: tool.name, detail: tool.detail },
          {
            kind: "log",
            entry: {
              kind: "tool_call",
              apartmentTypeId: type.id,
              toolName: tool.name,
              message: `${type.label}: ${tool.detail}`
            }
          }
        ]
      });
      events.push({
        atMs: t + TOOL_MS - 120,
        ops: [{ kind: "tool_finish", id: type.id, name: tool.name, status: "ok" }]
      });
    }

    // Verify with the engine.
    t += TOOL_MS;
    events.push({
      atMs: t,
      ops: [
        {
          kind: "type_status",
          id: type.id,
          status: "verifying",
          lastMessage: "Verifying IO capacity with the engine"
        },
        {
          kind: "tool_start",
          id: type.id,
          name: "verify_capacity",
          detail: "Recalculating IO utilisation against domain rules"
        },
        {
          kind: "log",
          entry: {
            kind: "verifier",
            apartmentTypeId: type.id,
            toolName: "verify_capacity",
            verifierStatus: "running",
            message: `${type.label}: recalculating IO utilisation against domain rules`
          }
        }
      ]
    });

    const reviseThisType = index === 0 && labels.length >= 1;

    if (reviseThisType) {
      // The first type demonstrates the propose -> verify -> revise -> emit loop.
      t += TOOL_MS;
      events.push({
        atMs: t,
        ops: [
          {
            kind: "tool_finish",
            id: type.id,
            name: "verify_capacity",
            status: "error"
          },
          {
            kind: "log",
            entry: {
              kind: "verifier",
              apartmentTypeId: type.id,
              toolName: "verify_capacity",
              verifierStatus: "mismatch",
              message: `${type.label}: engine flagged a controller near IO over-utilisation`
            }
          }
        ]
      });
      t += 520;
      events.push({
        atMs: t,
        ops: [
          {
            kind: "type_status",
            id: type.id,
            status: "revising",
            iteration: 2,
            lastMessage: "Revising the proposal to resolve the mismatch"
          },
          {
            kind: "tool_start",
            id: type.id,
            name: "pack_controllers",
            detail: "Re-packing zones to clear the IO mismatch"
          },
          {
            kind: "log",
            entry: {
              kind: "revise",
              apartmentTypeId: type.id,
              toolName: "pack_controllers",
              message: `${type.label}: re-packing zones to clear the IO mismatch`
            }
          }
        ]
      });
      t += TOOL_MS;
      events.push({
        atMs: t,
        ops: [
          { kind: "tool_finish", id: type.id, name: "pack_controllers", status: "ok" },
          {
            kind: "type_status",
            id: type.id,
            status: "verifying",
            lastMessage: "Re-verifying IO capacity with the engine"
          },
          {
            kind: "tool_start",
            id: type.id,
            name: "verify_capacity",
            detail: "Re-checking IO utilisation after revision"
          },
          {
            kind: "log",
            entry: {
              kind: "verifier",
              apartmentTypeId: type.id,
              toolName: "verify_capacity",
              verifierStatus: "running",
              message: `${type.label}: re-checking IO utilisation after revision`
            }
          }
        ]
      });
    }

    // Verify passes.
    t += TOOL_MS;
    events.push({
      atMs: t,
      ops: [
        { kind: "tool_finish", id: type.id, name: "verify_capacity", status: "ok" },
        {
          kind: "log",
          entry: {
            kind: "verifier",
            apartmentTypeId: type.id,
            toolName: "verify_capacity",
            verifierStatus: "ok",
            message: `${type.label}: IO utilisation within capacity`
          }
        }
      ]
    });

    // Derive infrastructure, then emit.
    t += 360;
    events.push({
      atMs: t,
      ops: [
        {
          kind: "tool_start",
          id: type.id,
          name: "derive_infrastructure",
          detail: "Deriving Netmasters, switches, PSUs and enclosures"
        },
        {
          kind: "log",
          entry: {
            kind: "tool_call",
            apartmentTypeId: type.id,
            toolName: "derive_infrastructure",
            message: `${type.label}: deriving Netmasters, switches, PSUs and enclosures`
          }
        }
      ]
    });
    t += TOOL_MS;
    events.push({
      atMs: t,
      ops: [
        {
          kind: "tool_finish",
          id: type.id,
          name: "derive_infrastructure",
          status: "ok"
        },
        {
          kind: "type_status",
          id: type.id,
          status: "done",
          lastMessage: "Specification emitted"
        },
        {
          kind: "log",
          entry: {
            kind: "type_done",
            apartmentTypeId: type.id,
            message: `${type.label}: specification emitted`
          }
        }
      ]
    });

    lastTypeEndMs = Math.max(lastTypeEndMs, t);
  });

  // Overseer aggregation phase.
  const aggregateStart = lastTypeEndMs + 500;
  events.push({
    atMs: aggregateStart,
    ops: [
      { kind: "phase", phase: "aggregating" },
      {
        kind: "overseer",
        value: {
          status: "running",
          message: "Aggregating per-type specifications into the project",
          stage: "cross_type_review",
          pass: 1,
          passCount: 2,
          canStillChange:
            "Cross-type review can flag an apartment for a focused re-check; verified results remain available to inspect meanwhile."
        }
      },
      {
        kind: "log",
        entry: {
          kind: "aggregating",
          message:
            "Overseer is aggregating per-type specifications and cross-checking totals"
        }
      }
    ]
  });

  const doneAt = aggregateStart + 2200;
  events.push({
    atMs: doneAt,
    ops: [
      { kind: "phase", phase: "done" },
      {
        kind: "overseer",
        value: {
          status: "done",
          message: "Project specification complete",
          stage: "project_overseer",
          pass: 1,
          passCount: 1
        }
      },
      {
        kind: "log",
        entry: {
          kind: "done",
          message: "Specification complete — opening the workspace"
        }
      }
    ]
  });

  return { events, totalMs: doneAt };
}

function buildMockFailurePlan(
  labels: { id: string; label: string }[],
  error: SpecifyJobError
): { events: MockEvent[]; totalMs: number } {
  const events: MockEvent[] = [];
  const first = labels[0];

  if (first) {
    events.push({
      atMs: 200,
      ops: [
        {
          kind: "type_status",
          id: first.id,
          status: "running",
          iteration: 1,
          lastMessage: "Proposing the controls specification"
        },
        {
          kind: "log",
          entry: {
            kind: "type_started",
            apartmentTypeId: first.id,
            message: `Specifying ${first.label}`
          }
        }
      ]
    });
    events.push({
      atMs: 1400,
      ops: [
        {
          kind: "type_status",
          id: first.id,
          status: "failed",
          lastMessage: error.message
        }
      ]
    });
  }

  const failAt = 1800;
  events.push({
    atMs: failAt,
    ops: [
      { kind: "phase", phase: "done" },
      {
        kind: "overseer",
        value: { status: "done", message: "Specification failed" }
      },
      {
        kind: "log",
        entry: { kind: "error", message: error.message }
      }
    ]
  });

  return { events, totalMs: failAt };
}

function readMockSpecifyJob(jobId: string): SpecifyJobSnapshot {
  const job = mockJobs.get(jobId);

  if (!job) {
    throw new Error(`Unknown mock specification job: ${jobId}.`);
  }

  const elapsedMs = Date.now() - job.createdAtMs;
  const createdAt = new Date(job.createdAtMs).toISOString();
  const types = new Map<string, SpecifyTypeProgress>(
    job.labels.map((entry) => [
      entry.id,
      {
        apartmentTypeId: entry.id,
        label: entry.label,
        status: "pending" as SpecifyTypeStatus,
        iteration: 0,
        toolCalls: [],
        lastMessage: "Queued for specification"
      }
    ])
  );
  let overseer: SpecifyOverseer = {
    status: "pending",
    message: "Waiting for per-type specifications"
  };
  let phase: SpecifyJobProgress["phase"] = "specifying";
  const log: SpecifyLogEntry[] = [];
  let lastEventMs = 0;

  const sorted = [...job.events].sort((a, b) => a.atMs - b.atMs);

  for (const event of sorted) {
    if (event.atMs > elapsedMs) {
      break;
    }

    lastEventMs = event.atMs;

    for (const op of event.ops) {
      applyMockOp(op, {
        types,
        setOverseer: (next) => {
          overseer = next;
        },
        setPhase: (next) => {
          phase = next;
        },
        appendLog: (entry) => {
          log.push({
            ...entry,
            seq: log.length + 1,
            ts: new Date(job.createdAtMs + event.atMs).toISOString()
          });
        }
      });
    }
  }

  const finished = elapsedMs >= job.totalMs;
  const status: SpecifyJobStatus = !finished
    ? "running"
    : job.error
      ? "failed"
      : "succeeded";
  const updatedAtMs =
    job.createdAtMs + (finished ? job.totalMs : Math.max(lastEventMs, 0));
  const completedTypes = mockCompletedTypes(job, types, log);

  return {
    jobId: job.jobId,
    status,
    createdAt,
    updatedAt: new Date(updatedAtMs).toISOString(),
    progress: {
      phase,
      types: job.labels.map((entry) => types.get(entry.id)!),
      completedTypes,
      overseer,
      log
    },
    ...(status === "failed" && job.error ? { error: job.error } : {}),
    ...(status === "succeeded" && job.result !== undefined
      ? { result: job.result }
      : {})
  };
}

function applyMockOp(
  op: MockOp,
  context: {
    types: Map<string, SpecifyTypeProgress>;
    setOverseer: (next: SpecifyOverseer) => void;
    setPhase: (next: SpecifyJobProgress["phase"]) => void;
    appendLog: (entry: Omit<SpecifyLogEntry, "seq" | "ts">) => void;
  }
): void {
  switch (op.kind) {
    case "type_status": {
      const type = context.types.get(op.id);

      if (type) {
        type.status = op.status;

        if (op.iteration !== undefined) {
          type.iteration = op.iteration;
        }

        if (op.lastMessage !== undefined) {
          type.lastMessage = op.lastMessage;
        }
      }

      break;
    }
    case "tool_start": {
      const type = context.types.get(op.id);

      if (type) {
        type.toolCalls = [
          ...type.toolCalls.filter((call) => call.name !== op.name),
          {
            name: op.name,
            status: "running",
            ...(op.detail !== undefined ? { detail: op.detail } : {})
          }
        ];
      }

      break;
    }
    case "tool_finish": {
      const type = context.types.get(op.id);

      if (type) {
        type.toolCalls = type.toolCalls.map((call) =>
          call.name === op.name ? { ...call, status: op.status } : call
        );
      }

      break;
    }
    case "overseer": {
      context.setOverseer(op.value);

      break;
    }
    case "phase": {
      context.setPhase(op.phase);

      break;
    }
    case "log": {
      context.appendLog(op.entry);

      break;
    }
    default: {
      const exhaustive: never = op;

      void exhaustive;
    }
  }
}

function mockCompletedTypes(
  job: MockJob,
  types: ReadonlyMap<string, SpecifyTypeProgress>,
  log: readonly SpecifyLogEntry[]
): SpecifyCompletedType[] {
  const fixture = extractSpecifyResultFixture(job.result);
  const solutions =
    fixture && typeof fixture === "object" && "apartmentSolutions" in fixture
      ? (fixture as { apartmentSolutions?: unknown }).apartmentSolutions
      : undefined;

  if (!Array.isArray(solutions)) {
    return [];
  }

  return job.labels.flatMap((label) => {
    if (types.get(label.id)?.status !== "done") {
      return [];
    }

    const solution = solutions.find(
      (candidate) =>
        candidate &&
        typeof candidate === "object" &&
        "apartmentTypeId" in candidate &&
        candidate.apartmentTypeId === label.id
    );

    if (!solution) {
      return [];
    }

    const completedAt =
      [...log]
        .reverse()
        .find(
          (entry) => entry.kind === "type_done" && entry.apartmentTypeId === label.id
        )?.ts ?? new Date(job.createdAtMs).toISOString();

    return [
      {
        apartmentTypeId: label.id,
        completedAt,
        solutionHash: `mock-${label.id}`,
        source: "specialist" as const,
        solution
      }
    ];
  });
}

/** Test-only: clear mock job state between runs. */
export function __resetMockSpecifyJobs(): void {
  mockJobs.clear();
}
